import { z } from 'zod';
import { ulidSchema } from './id';

/**
 * Board backgrounds — the shared contract layer for B42's board-scoped
 * background setting.
 *
 * Unlike theme and colour-blind mode, a board background is shared board state:
 * every board member sees the same value. The read schema covers every stored
 * `boards.background_type`, including `custom_upload`, because that value is
 * already in the database. The B42 write schema is narrower and accepts only a
 * colour or stock-photo reference; custom uploads belong to `FR-EXP-026`, not
 * this block.
 */

/** `boards.background_colour varchar(32)`. */
export const BOARD_BACKGROUND_COLOUR_MAX_LENGTH = 32;

/** `boards.background_photo_ref varchar(2048)`. */
export const BOARD_BACKGROUND_PHOTO_REF_MAX_LENGTH = 2048;

/** `boards.background_photo_attribution varchar(512)`. */
export const BOARD_BACKGROUND_PHOTO_ATTRIBUTION_MAX_LENGTH = 512;

/** `custom_board_backgrounds.image_url varchar(2048)`, storing an S3 object key. */
export const BOARD_BACKGROUND_CUSTOM_STORAGE_KEY_MAX_LENGTH = 2048;

/** Browser-uploaded custom backgrounds for `FR-EXP-026` are images only. */
export const BOARD_BACKGROUND_UPLOAD_CONTENT_TYPES = [
  'image/jpeg',
  'image/png',
  'image/webp',
  'image/gif',
] as const;

/**
 * `FR-EXP-026` custom background upload cap. 10 MiB is intentionally separate
 * from the 250 MiB attachment cap: a board background is rendered eagerly for
 * every board reader, not fetched as an explicit attachment action.
 */
export const BOARD_BACKGROUND_UPLOAD_MAX_BYTES = 10 * 1024 * 1024;

/** Client label only; the object key keeps only a sanitised extension. */
export const BOARD_BACKGROUND_UPLOAD_FILE_NAME_MAX_LENGTH = 255;

/** `boards.background_type` — `ck_boards_background_type` restated. */
export const boardBackgroundTypeSchema = z.enum(['colour', 'photo', 'custom_upload']);

export type BoardBackgroundType = z.infer<typeof boardBackgroundTypeSchema>;

/** `boards.background_render_mode` — `ck_boards_background_render_mode`. */
export const boardBackgroundRenderModeSchema = z.enum(['tiled', 'full_cover']);

export type BoardBackgroundRenderMode = z.infer<typeof boardBackgroundRenderModeSchema>;

/** `boards.background_text_treatment` — `ck_boards_background_text_treatment`. */
export const boardBackgroundTextTreatmentSchema = z.enum(['light', 'dark']);

export type BoardBackgroundTextTreatment = z.infer<
  typeof boardBackgroundTextTreatmentSchema
>;

export const boardBackgroundUploadContentTypeSchema = z.enum(
  BOARD_BACKGROUND_UPLOAD_CONTENT_TYPES,
);

export type BoardBackgroundUploadContentType = z.infer<
  typeof boardBackgroundUploadContentTypeSchema
>;

/**
 * A board background as read routes return it.
 *
 * The nullable fields mirror nullable columns. In particular, an untouched
 * board is legally `type: 'colour'` with `colour: null`; the server must be
 * able to render that row without inventing a value at the schema boundary.
 */
export const boardBackgroundSchema = z.discriminatedUnion('type', [
  z.object({
    type: z.literal('colour'),
    colour: z.string().max(BOARD_BACKGROUND_COLOUR_MAX_LENGTH).nullable(),
    renderMode: boardBackgroundRenderModeSchema,
    textTreatment: boardBackgroundTextTreatmentSchema,
  }),
  z.object({
    type: z.literal('photo'),
    photoRef: z.string().max(BOARD_BACKGROUND_PHOTO_REF_MAX_LENGTH).nullable(),
    photoAttribution: z
      .string()
      .max(BOARD_BACKGROUND_PHOTO_ATTRIBUTION_MAX_LENGTH)
      .nullable(),
    renderMode: boardBackgroundRenderModeSchema,
    textTreatment: boardBackgroundTextTreatmentSchema,
  }),
  z.object({
    type: z.literal('custom_upload'),
    backgroundCustomId: ulidSchema.nullable(),
    renderMode: boardBackgroundRenderModeSchema,
    textTreatment: boardBackgroundTextTreatmentSchema,
  }),
]);

export type BoardBackground = z.infer<typeof boardBackgroundSchema>;

/**
 * `PUT /boards/:id/background`, and the optional creation-time background
 * body B42 adds to `POST /boards`.
 *
 * This is a whole-value write, so render mode and text treatment are required
 * beside the source-specific payload. Strict variants make unsupported custom
 * uploads and stray DB-column spellings fail validation rather than disappear.
 */
export const boardBackgroundPutSchema = z.discriminatedUnion('type', [
  z.strictObject({
    type: z.literal('colour'),
    colour: z.string().min(1).max(BOARD_BACKGROUND_COLOUR_MAX_LENGTH),
    renderMode: boardBackgroundRenderModeSchema,
    textTreatment: boardBackgroundTextTreatmentSchema,
  }),
  z.strictObject({
    type: z.literal('photo'),
    photoRef: z.string().min(1).max(BOARD_BACKGROUND_PHOTO_REF_MAX_LENGTH),
    photoAttribution: z
      .string()
      .max(BOARD_BACKGROUND_PHOTO_ATTRIBUTION_MAX_LENGTH)
      .nullable(),
    renderMode: boardBackgroundRenderModeSchema,
    textTreatment: boardBackgroundTextTreatmentSchema,
  }),
]);

export type BoardBackgroundPut = z.infer<typeof boardBackgroundPutSchema>;

export const boardBackgroundUploadPresignBodySchema = z.strictObject({
  fileName: z.string().min(1).max(BOARD_BACKGROUND_UPLOAD_FILE_NAME_MAX_LENGTH),
  contentType: boardBackgroundUploadContentTypeSchema,
  sizeBytes: z.number().int().positive().max(BOARD_BACKGROUND_UPLOAD_MAX_BYTES),
});

export type BoardBackgroundUploadPresignBody = z.infer<
  typeof boardBackgroundUploadPresignBodySchema
>;

export const boardBackgroundUploadPresignSchema = z.strictObject({
  storageKey: z.string().min(1).max(BOARD_BACKGROUND_CUSTOM_STORAGE_KEY_MAX_LENGTH),
  url: z.url(),
  headers: z.record(z.string(), z.string()),
  expiresInSeconds: z.number().int().positive(),
  acceptedContentTypes: z.tuple([
    z.literal('image/jpeg'),
    z.literal('image/png'),
    z.literal('image/webp'),
    z.literal('image/gif'),
  ]),
  maxSizeBytes: z.literal(BOARD_BACKGROUND_UPLOAD_MAX_BYTES),
});

export type BoardBackgroundUploadPresign = z.infer<
  typeof boardBackgroundUploadPresignSchema
>;

export const boardBackgroundUploadConfirmBodySchema = z.strictObject({
  storageKey: z.string().min(1).max(BOARD_BACKGROUND_CUSTOM_STORAGE_KEY_MAX_LENGTH),
  renderMode: boardBackgroundRenderModeSchema,
  textTreatment: boardBackgroundTextTreatmentSchema,
  applyToBoard: z.boolean(),
});

export type BoardBackgroundUploadConfirmBody = z.infer<
  typeof boardBackgroundUploadConfirmBodySchema
>;

/**
 * `POST /boards/:id/background/custom/:backgroundId/apply` — `FR-EXP-026`
 * AC1's "reusable" half and AC2 in one shot. `backgroundId` names an existing,
 * already-confirmed `custom_board_backgrounds` row (this board's own upload,
 * a sibling board's, or the one already applied here); no fresh S3 object is
 * involved, so — unlike confirm — this never writes `custom_board_backgrounds`
 * itself. A whole-value write, matching `boardBackgroundPutSchema`'s own
 * shape: render mode and text treatment are required beside the reference,
 * because applying the SAME `backgroundId` already on the board with a
 * DIFFERENT `renderMode`/`textTreatment` is exactly AC2's "switch between
 * tiled and full-cover and between light and dark board text".
 */
export const boardBackgroundCustomApplyBodySchema = z.strictObject({
  renderMode: boardBackgroundRenderModeSchema,
  textTreatment: boardBackgroundTextTreatmentSchema,
});

export type BoardBackgroundCustomApplyBody = z.infer<
  typeof boardBackgroundCustomApplyBodySchema
>;

export const customBoardBackgroundSchema = z.strictObject({
  id: ulidSchema,
  renderMode: boardBackgroundRenderModeSchema,
  textTreatment: boardBackgroundTextTreatmentSchema,
  createdAt: z.string().min(1),
  updatedAt: z.string().min(1),
});

export type CustomBoardBackground = z.infer<typeof customBoardBackgroundSchema>;

export const customBoardBackgroundListSchema = z.array(customBoardBackgroundSchema);

export type CustomBoardBackgroundList = z.infer<typeof customBoardBackgroundListSchema>;

export const boardBackgroundUploadConfirmResponseSchema = z.strictObject({
  background: customBoardBackgroundSchema,
  boardBackground: boardBackgroundSchema,
});

export type BoardBackgroundUploadConfirmResponse = z.infer<
  typeof boardBackgroundUploadConfirmResponseSchema
>;
